import { html, nothing, type TemplateResult } from "lit";
import { t } from "../i18n.ts";

export type QuickSettingsProps = {
  formValue: Record<string, unknown> | null;
  disabled: boolean;
  onPatch: (path: Array<string | number>, value: unknown) => void;
  onSectionChange: (section: string | null) => void;
};

// Helpers to read/write nested config values
function getDeep(obj: Record<string, unknown> | null, path: string[]): unknown {
  if (!obj) return undefined;
  let cur: unknown = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function card(title: string, icon: TemplateResult, body: TemplateResult): TemplateResult {
  return html`
    <div class="qs-card">
      <div class="qs-card__header">
        <span class="qs-card__icon">${icon}</span>
        <span class="qs-card__title">${title}</span>
      </div>
      <div class="qs-card__body">${body}</div>
    </div>
  `;
}

// Icons (Lucide-style SVG)
const icons = {
  ai: html`
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path
        d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z"
      />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
    </svg>
  `,
  conn: html`
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <rect x="2" y="2" width="20" height="8" rx="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" />
      <line x1="6" y1="6" x2="6.01" y2="6" />
      <line x1="6" y1="18" x2="6.01" y2="18" />
    </svg>
  `,
  apps: html`
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  `,
  updates: html`
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  `,
  log: html`
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  `,
};

// Card 1: AI Assistant
function renderAiCard(props: QuickSettingsProps): TemplateResult {
  const model = getDeep(props.formValue, ["agents", "defaults", "model", "primary"]) as
    | string
    | undefined;
  const workspace = getDeep(props.formValue, ["agents", "defaults", "workspace"]) as
    | string
    | undefined;

  return card(
    t("qs.ai"),
    icons.ai,
    html`
      <div class="qs-fields">
        <div class="cfg-field">
          <label class="cfg-field__label">${t("qs.ai.model")}</label>
          <div class="cfg-field__help">${t("qs.ai.model_help")}</div>
          <div class="cfg-input-wrap">
            <input
              type="text"
              class="cfg-input"
              placeholder="e.g. claude-sonnet-4-20250514"
              .value=${model ?? ""}
              ?disabled=${props.disabled}
              @change=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value.trim();
                props.onPatch(["agents", "defaults", "model", "primary"], v || undefined);
              }}
            />
          </div>
        </div>
        <div class="cfg-field">
          <label class="cfg-field__label">${t("qs.ai.workspace")}</label>
          <div class="cfg-field__help">${t("qs.ai.workspace_help")}</div>
          <div class="cfg-input-wrap">
            <input
              type="text"
              class="cfg-input"
              placeholder="~/nabe-workspace"
              .value=${workspace ?? ""}
              ?disabled=${props.disabled}
              @change=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value.trim();
                props.onPatch(["agents", "defaults", "workspace"], v || undefined);
              }}
            />
          </div>
        </div>
      </div>
    `,
  );
}

// Card 2: Connection
function renderConnectionCard(props: QuickSettingsProps): TemplateResult {
  const port = getDeep(props.formValue, ["gateway", "port"]) as number | undefined;
  const bind = getDeep(props.formValue, ["gateway", "bind"]) as string | undefined;
  const authMode = getDeep(props.formValue, ["gateway", "auth", "mode"]) as string | undefined;
  const password = getDeep(props.formValue, ["gateway", "auth", "password"]) as string | undefined;
  const isPasswordAuth = authMode === "password";

  const bindOptions: Array<{ value: string; label: string }> = [
    { value: "loopback", label: t("qs.conn.bind.loopback") },
    { value: "lan", label: t("qs.conn.bind.lan") },
    { value: "tailnet", label: t("qs.conn.bind.tailnet") },
    { value: "auto", label: t("qs.conn.bind.auto") },
  ];

  return card(
    t("qs.conn"),
    icons.conn,
    html`
      <div class="qs-fields">
        <div class="cfg-field">
          <label class="cfg-field__label">${t("qs.conn.port")}</label>
          <div class="cfg-field__help">${t("qs.conn.port_help")}</div>
          <div class="cfg-input-wrap">
            <input
              type="number"
              class="cfg-input"
              placeholder="18789"
              .value=${port != null ? String(port) : ""}
              ?disabled=${props.disabled}
              @change=${(e: Event) => {
                const raw = (e.target as HTMLInputElement).value;
                const num = raw === "" ? undefined : Number(raw);
                props.onPatch(["gateway", "port"], num);
              }}
            />
          </div>
        </div>
        <div class="cfg-field">
          <label class="cfg-field__label">${t("qs.conn.bind")}</label>
          <div class="cfg-field__help">${t("qs.conn.bind_help")}</div>
          <div class="cfg-segmented">
            ${bindOptions.map(
              (opt) => html`
                <button
                  type="button"
                  class="cfg-segmented__btn ${(bind ?? "auto") === opt.value ? "active" : ""}"
                  ?disabled=${props.disabled}
                  @click=${() => props.onPatch(["gateway", "bind"], opt.value)}
                >${opt.label}</button>
              `,
            )}
          </div>
        </div>
        <label class="cfg-toggle-row ${props.disabled ? "disabled" : ""}">
          <div class="cfg-toggle-row__content">
            <span class="cfg-toggle-row__label">${t("qs.conn.auth")}</span>
            <span class="cfg-toggle-row__help">${t("qs.conn.auth_help")}</span>
          </div>
          <div class="cfg-toggle">
            <input
              type="checkbox"
              .checked=${isPasswordAuth}
              ?disabled=${props.disabled}
              @change=${(e: Event) => {
                const checked = (e.target as HTMLInputElement).checked;
                props.onPatch(["gateway", "auth", "mode"], checked ? "password" : undefined);
              }}
            />
            <span class="cfg-toggle__track"></span>
          </div>
        </label>
        ${
          isPasswordAuth
            ? html`
            <div class="cfg-field">
              <label class="cfg-field__label">${t("qs.conn.password")}</label>
              <div class="cfg-input-wrap">
                <input
                  type="password"
                  class="cfg-input"
                  placeholder="••••"
                  .value=${password ?? ""}
                  ?disabled=${props.disabled}
                  @change=${(e: Event) => {
                    const v = (e.target as HTMLInputElement).value.trim();
                    props.onPatch(["gateway", "auth", "password"], v || undefined);
                  }}
                />
              </div>
            </div>
          `
            : nothing
        }
      </div>
    `,
  );
}

// Card 3: Messaging Apps (shortcut to Channels tab)
function renderAppsCard(props: QuickSettingsProps): TemplateResult {
  return card(
    t("qs.apps"),
    icons.apps,
    html`
      <div class="qs-fields">
        <p class="cfg-field__help" style="margin:0">${t("qs.apps.subtitle")}</p>
        <button
          type="button"
          class="qs-link-btn"
          @click=${() => props.onSectionChange("channels")}
        >${t("qs.apps.configure")} →</button>
      </div>
    `,
  );
}

// Card 4: Updates
function renderUpdatesCard(props: QuickSettingsProps): TemplateResult {
  const channel = getDeep(props.formValue, ["update", "channel"]) as string | undefined;
  const autoCheck = getDeep(props.formValue, ["update", "checkOnStart"]) as boolean | undefined;

  const channelOptions = ["stable", "beta", "dev"];

  return card(
    t("qs.updates"),
    icons.updates,
    html`
      <div class="qs-fields">
        <div class="cfg-field">
          <label class="cfg-field__label">${t("qs.updates.channel")}</label>
          <div class="cfg-field__help">${t("qs.updates.channel_help")}</div>
          <div class="cfg-segmented">
            ${channelOptions.map(
              (opt) => html`
                <button
                  type="button"
                  class="cfg-segmented__btn ${(channel ?? "stable") === opt ? "active" : ""}"
                  ?disabled=${props.disabled}
                  @click=${() => props.onPatch(["update", "channel"], opt)}
                >${opt}</button>
              `,
            )}
          </div>
        </div>
        <label class="cfg-toggle-row ${props.disabled ? "disabled" : ""}">
          <div class="cfg-toggle-row__content">
            <span class="cfg-toggle-row__label">${t("qs.updates.auto")}</span>
            <span class="cfg-toggle-row__help">${t("qs.updates.auto_help")}</span>
          </div>
          <div class="cfg-toggle">
            <input
              type="checkbox"
              .checked=${autoCheck ?? false}
              ?disabled=${props.disabled}
              @change=${(e: Event) => {
                props.onPatch(["update", "checkOnStart"], (e.target as HTMLInputElement).checked);
              }}
            />
            <span class="cfg-toggle__track"></span>
          </div>
        </label>
      </div>
    `,
  );
}

// Card 5: Logging
function renderLoggingCard(props: QuickSettingsProps): TemplateResult {
  const level = getDeep(props.formValue, ["logging", "level"]) as string | undefined;

  const levels = ["silent", "error", "warn", "info", "debug", "trace"];

  return card(
    t("qs.log"),
    icons.log,
    html`
      <div class="qs-fields">
        <div class="cfg-field">
          <label class="cfg-field__label">${t("qs.log.level")}</label>
          <div class="cfg-field__help">${t("qs.log.level_help")}</div>
          <div class="cfg-segmented cfg-segmented--wrap">
            ${levels.map(
              (opt) => html`
                <button
                  type="button"
                  class="cfg-segmented__btn ${(level ?? "info") === opt ? "active" : ""}"
                  ?disabled=${props.disabled}
                  @click=${() => props.onPatch(["logging", "level"], opt)}
                >${opt}</button>
              `,
            )}
          </div>
        </div>
      </div>
    `,
  );
}

export function renderQuickSettings(props: QuickSettingsProps): TemplateResult {
  return html`
    <div class="qs">
      <div class="qs__header">
        <h2 class="qs__title">${t("qs.title")}</h2>
        <p class="qs__subtitle">${t("qs.subtitle")}</p>
      </div>
      <div class="qs__grid">
        ${renderAiCard(props)}
        ${renderConnectionCard(props)}
        ${renderAppsCard(props)}
        ${renderUpdatesCard(props)}
        ${renderLoggingCard(props)}
      </div>
      <button
        type="button"
        class="qs-all-btn"
        @click=${() => props.onSectionChange("env")}
      >${t("qs.all_settings")} →</button>
    </div>
  `;
}
